import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BookOpen, FileText, CircleHelp, Clock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/components/Common";
import { Page, PageHeader, Loading, ErrorNote, RichText } from "@/components/Common";

const FUTURE = ["تشریح", "مشکل الفاظ", "English مفہوم", "فنی و ادبی تجزیہ", "اہم اشعار", "UGC NET نکات", "PYQs"];

const Question = ({ item, index }) => {
  const [show, setShow] = useState(false);
  if (typeof item === "string") {
    return <li className="text-lg text-[#33200f] leading-[2.2]">{item}</li>;
  }
  return (
    <div className="border border-[#d4af37]/30 rounded-xl p-5 bg-[#fdfbf7]" data-testid={`question-${index}`}>
      <p className="text-lg font-semibold text-[#2a1508] leading-[2.2]">سوال {index + 1}: {item.q}</p>
      {item.options?.length > 0 && (
        <ul className="mt-3 grid sm:grid-cols-2 gap-2">
          {item.options.map((o, i) => (
            <li
              key={i}
              className={`px-4 py-2 rounded-lg border text-base leading-[2] transition-colors ${
                show && i === item.answer ? "bg-[#e6f2e6] border-green-700/40 text-green-900" : "border-[#c5a059]/40 text-[#4a2b16]"
              }`}
            >
              {o}
            </li>
          ))}
        </ul>
      )}
      <button
        data-testid={`question-answer-toggle-${index}`}
        onClick={() => setShow(!show)}
        className="mt-3 text-base px-4 py-1 rounded-full border border-[#2a1508] text-[#2a1508] hover:bg-[#2a1508] hover:text-[#f5efe3] transition-colors leading-[2]"
      >
        {show ? "جواب چھپائیں" : "جواب دیکھیں"}
      </button>
    </div>
  );
};

export default function WorkPage() {
  const { slug } = useParams();
  const [work, setWork] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setWork(null);
    setErr(false);
    api.get(`/works/${slug}`).then((r) => setWork(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <Page><ErrorNote message="متن نہیں ملا" /></Page>;
  if (!work) return <Page><Loading /></Page>;

  const crumbs = [];
  if (work.unitNum) {
    crumbs.push({ label: "UGC NET اردو", to: "/ugc-net" }, { label: `یونٹ ${work.unitNum}`, to: `/ugc-net/unit/${work.unitNum}` });
  }
  if (work.author) crumbs.push({ label: work.author.name, to: `/authors/${work.author.slug}` });
  crumbs.push({ label: work.title });

  return (
    <Page className="max-w-4xl">
      <PageHeader title={work.title} crumbs={crumbs} testid="work-title" />

      {work.author && (
        <Link to={`/authors/${work.author.slug}`} data-testid="work-author-link" className="inline-flex items-center gap-2 mb-8 px-5 py-2 rounded-full bg-[#e8dfcc] border border-[#c5a059]/60 text-[#2a1508] hover:border-[#d4af37] transition-colors leading-[2]">
          شاعر / مصنف: <span className="font-semibold">{work.author.name}</span>
          <span className="font-ui text-sm text-[#5c3a21]">{work.author.years}</span>
        </Link>
      )}

      <Tabs defaultValue="matn" dir="rtl" data-testid="work-tabs">
        <TabsList className="w-full justify-start gap-2 bg-transparent p-0 h-auto flex-wrap">
          {[
            { v: "matn", label: "اصل متن", icon: BookOpen },
            { v: "khulasa", label: "خلاصہ", icon: FileText },
            { v: "sawalat", label: "سوالات", icon: CircleHelp },
          ].map(({ v, label, icon: Icon }) => (
            <TabsTrigger
              key={v}
              value={v}
              data-testid={`work-tab-${v}`}
              className="px-6 py-2.5 rounded-full border border-[#c5a059]/60 text-[#2a1508] text-base leading-[2] data-[state=active]:bg-[#2a1508] data-[state=active]:text-[#f5efe3] data-[state=active]:border-[#2a1508] bg-[#fdfbf7]"
            >
              <Icon size={16} className="me-1.5" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="matn" className="mt-6">
          <div className="bg-[#fdfbf7] border border-[#d4af37]/40 rounded-2xl p-6 sm:p-10 text-center shadow-[0_4px_20px_rgba(42,21,8,0.06)]" data-testid="work-matn">
            {work.pdfUrl && work.pdfUrl.includes("archive.org/details/") && (
              <div className="mb-6" data-testid="work-archive-reader">
                <p className="text-lg text-[#5c3a21] leading-[2] mb-3">مکمل متن — نیچے ریڈر میں پڑھیں (Internet Archive):</p>
                <iframe
                  src={work.pdfUrl.replace("/details/", "/embed/")}
                  title="مکمل متن"
                  className="w-full rounded-xl border border-[#d4af37]/50"
                  style={{ height: "70vh" }}
                  allowFullScreen
                />
              </div>
            )}
            {work.pdfUrl && !work.pdfUrl.includes("archive.org/details/") && (
              <a href={work.pdfUrl} target="_blank" rel="noopener noreferrer" data-testid="work-pdf-link" className="inline-flex items-center gap-2 mb-6 px-6 py-2.5 rounded-full bg-[#2a1508] text-[#f5efe3] hover:bg-[#4a2b16] transition-colors">
                <BookOpen size={16} /> مکمل متن / PDF کھولیں
              </a>
            )}
            {work.matn?.split("\n").filter(Boolean).map((line, i) => (
              <p key={i} className="text-xl sm:text-2xl text-[#2a1508] leading-[2.6]">{line}</p>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="khulasa" className="mt-6">
          <div className="bg-[#fdfbf7] border border-[#d4af37]/40 rounded-2xl p-6 sm:p-10 shadow-[0_4px_20px_rgba(42,21,8,0.06)]" data-testid="work-khulasa">
            <RichText text={work.khulasa || "خلاصہ جلد شامل کیا جائے گا"} />
          </div>
        </TabsContent>

        <TabsContent value="sawalat" className="mt-6">
          <div className="space-y-4" data-testid="work-sawalat">
            {work.sawalat?.length ? (
              work.sawalat.map((s, i) => <Question key={i} item={s} index={i} />)
            ) : (
              <p className="text-lg text-[#5c3a21] bg-[#fdfbf7] border border-dashed border-[#c5a059]/60 rounded-xl p-5">سوالات جلد شامل کیے جائیں گے</p>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {work.characters?.length > 0 && (
        <div className="mt-10" data-testid="work-characters">
          <h3 className="text-2xl font-bold text-[#2a1508] leading-[2] mb-4">کردار</h3>
          <div className="overflow-x-auto bg-[#fdfbf7] border border-[#d4af37]/40 rounded-2xl">
            <table className="w-full text-right">
              <thead>
                <tr className="bg-[#e8dfcc] text-[#2a1508]">
                  <th className="px-5 py-3 text-base font-bold leading-[2]">کردار</th>
                  <th className="px-5 py-3 text-base font-bold leading-[2]">تعارف</th>
                  <th className="px-5 py-3 text-base font-bold leading-[2]">کہانی میں کردار</th>
                </tr>
              </thead>
              <tbody>
                {work.characters.map((c, i) => (
                  <tr key={i} className="border-t border-[#d4af37]/30">
                    <td className="px-5 py-3 text-base font-semibold text-[#2a1508] leading-[2]">{c.name}</td>
                    <td className="px-5 py-3 text-base text-[#4a2b16] leading-[2.2]">{c.intro}</td>
                    <td className="px-5 py-3 text-base text-[#4a2b16] leading-[2.2]">{c.role}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-10">
        <h3 className="text-lg font-semibold text-[#5c3a21] leading-[2] mb-3">جلد شامل ہوگا:</h3>
        <div className="flex flex-wrap gap-2" data-testid="work-upcoming">
          {FUTURE.map((f) => (
            <span key={f} className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full border border-dashed border-[#c5a059]/70 text-[#5c3a21] text-base leading-[2]">
              <Clock size={13} /> {f}
            </span>
          ))}
        </div>
      </div>
    </Page>
  );
}
